/**
 * ELO Rating System
 * Standard chess ELO calculation for in-person games
 */

// K-factor: How much ratings change per game
const K_FACTOR = 32; // Standard for most players

/**
 * Calculate expected score for a player
 * @param {number} playerRating - Player's current rating
 * @param {number} opponentRating - Opponent's current rating
 * @returns {number} Expected score (0 to 1)
 */
export const calculateExpectedScore = (playerRating, opponentRating) => {
  return 1 / (1 + Math.pow(10, (opponentRating - playerRating) / 400));
};

/**
 * Calculate new ELO rating after a game
 * @param {number} currentRating - Player's current rating
 * @param {number} opponentRating - Opponent's rating
 * @param {number} actualScore - Actual score: 1 (win), 0.5 (draw), 0 (loss)
 * @returns {number} New rating (rounded to nearest integer)
 */
export const calculateNewRating = (currentRating, opponentRating, actualScore) => {
  const expectedScore = calculateExpectedScore(currentRating, opponentRating);
  const ratingChange = K_FACTOR * (actualScore - expectedScore);
  return Math.round(currentRating + ratingChange);
};

/**
 * Calculate rating changes for both players after a game
 * @param {number} player1Rating - Player 1's current rating
 * @param {number} player2Rating - Player 2's current rating
 * @param {string} result - Game result: 'PLAYER1_WIN', 'PLAYER2_WIN', 'DRAW'
 * @returns {object} { player1NewRating, player2NewRating, player1Change, player2Change }
 */
export const calculateRatingChanges = (player1Rating, player2Rating, result) => {
  let player1Score, player2Score;

  switch (result) {
    case 'PLAYER1_WIN':
      player1Score = 1;
      player2Score = 0;
      break;
    case 'PLAYER2_WIN':
      player1Score = 0;
      player2Score = 1;
      break;
    case 'DRAW':
      player1Score = 0.5;
      player2Score = 0.5;
      break;
    default:
      throw new Error('Invalid game result');
  }

  const player1NewRating = calculateNewRating(player1Rating, player2Rating, player1Score);
  const player2NewRating = calculateNewRating(player2Rating, player1Rating, player2Score);

  return {
    player1NewRating,
    player2NewRating,
    player1Change: player1NewRating - player1Rating,
    player2Change: player2NewRating - player2Rating,
  };
};

/**
 * Get default rating for new players
 * @returns {number} Default rating (1200)
 */
export const getDefaultRating = () => 1200;

/**
 * Format rating change for display
 * @param {number} change - Rating change amount
 * @returns {string} Formatted string like "+25" or "-15"
 */
export const formatRatingChange = (change) => {
  if (change > 0) return `+${change}`;
  return `${change}`;
};

